//export const { insert, findAll: loadEvents, remove } =  Crud( 'events');
import axios from "axios";
import {parseJwt} from "../helpers/authUtils";
import store from '../stores/index';

async function withPolling(callback, interval) {
    const data = await callback();
    if (!interval) return data;
    const obs = Vue.observable({data}), poll = () => {
        setTimeout(async () => {
            obs.data = {...(await callback())};
            poll();
        }, interval);
    };
    poll();
    return obs;
}

const _axios = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
    timeout: 100000, headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
}

_axios.interceptors.request.use(async config => {
    let token = localStorage.getItem('kc_token');
    const refreshToken = localStorage.getItem('kc_refresh_token');

    if (token) {
        const decoded = parseJwt(token);
        const isExpired = decoded.exp * 1000 < Date.now();

        if (isExpired && refreshToken) {
            if (isRefreshing) {
                // Attend la fin du refresh en cours
                token = await new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                });
            } else {
                isRefreshing = true;

                try {
                    const response = await fetch(
                        `${process.env.VUE_APP_KEYCLOAK_URL}/realms/${process.env.VUE_APP_KEYCLOAK_REALM}/protocol/openid-connect/token`,
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body: new URLSearchParams({
                                grant_type: 'refresh_token',
                                client_id: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
                                refresh_token: refreshToken,
                            }),
                        }
                    );

                    if (!response.ok) {
                        processQueue(new Error('Échec du refresh token'), null);
                        store.dispatch('auth/logout');
                        return Promise.reject(new Error('Échec du refresh token'));
                    }

                    const data = await response.json();
                    token = data.access_token;
                    const newDecoded = parseJwt(token);

                    localStorage.setItem('kc_token', token);
                    localStorage.setItem('kc_refresh_token', data.refresh_token);

                    store.commit('auth/SET_TOKEN', token);
                    store.commit('auth/SET_EXPIRES_IN', newDecoded.exp - Math.floor(Date.now() / 1000));

                    processQueue(null, token);
                } catch (e) {
                    processQueue(e, null);
                    store.dispatch('auth/logout');
                    return Promise.reject(e);
                } finally {
                    isRefreshing = false;
                }
            }
        }

        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
}, error => Promise.reject(error));

_axios.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const refreshToken = localStorage.getItem('kc_refresh_token');
            if (!refreshToken) {
                store.dispatch('auth/logout');
                return Promise.reject(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token) => {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(_axios(originalRequest));
                        },
                        reject,
                    });
                });
            }

            isRefreshing = true;

            try {
                const response = await fetch(
                    `${process.env.VUE_APP_KEYCLOAK_URL}/realms/${process.env.VUE_APP_KEYCLOAK_REALM}/protocol/openid-connect/token`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                        body: new URLSearchParams({
                            grant_type: 'refresh_token',
                            client_id: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
                            refresh_token: refreshToken,
                        }),
                    }
                );

                if (!response.ok) {
                    processQueue(new Error('Refresh token failed'), null);
                    store.dispatch('auth/logout');
                    return Promise.reject(error);
                }

                const data = await response.json();
                const newToken = data.access_token;
                const newDecoded = parseJwt(newToken);

                localStorage.setItem('kc_token', newToken);
                localStorage.setItem('kc_refresh_token', data.refresh_token);

                store.commit('auth/SET_TOKEN', newToken);
                store.commit('auth/SET_EXPIRES_IN', newDecoded.exp - Math.floor(Date.now() / 1000));

                _axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                processQueue(null, newToken);

                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return _axios(originalRequest); // ✅ rejoue la requête initiale
            } catch (err) {
                processQueue(err, null);
                store.dispatch('auth/logout');
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    }
);



export default _axios;

export const crud = (endpoint, interval) => ({
    async findAll(options = {}) {
        return await withPolling(() => _axios.get(endpoint+'/page', options), interval);
    },

    async findAllData(options = {}) {
        return await withPolling(() => _axios.get(endpoint, options), interval);
    },

    async findAllComming(options = {}) {
        return await withPolling(() => _axios.get(endpoint+'/all', options), interval);
    },

    async findAllMatricule(options = {}, matricule) {
        return await withPolling(() => _axios.get(endpoint+'/matricule/'+matricule, options), interval);
    },

    create(data, options = {}) {
        return _axios.post(endpoint, data, options).then(res => res.data);
    },

    save(data, options = {}) {
        return _axios.post(endpoint+'/save', data, options).then(res => res.data);
    },

    async findOne(id, options = {}) {
        return await _axios.get(`${endpoint}/${id}`, options);
    },

    update(data, options = {}) {
        return options.partial ?
            _axios.patch(`${endpoint}/${data.id}`, data, options).then(res => res.data) :
            _axios.put(`${endpoint}/${data.id}`, data, options).then(res => res.data);
    },

    async remove(id, options = {}) {
        return await _axios.delete(`${endpoint}/${id}`, options);
    },
});

export * as auth from './auth';
